import React, { Component } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { withToastManager } from 'react-toast-notifications';
import { Mutex } from 'async-mutex';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { connect } from 'react-redux';
import AuthContainer from '../components/AuthContainer';
import LabeledInput from '../components/LabeledInput';
import TwoFactorLogin from '../components/TwoFactorLogin';
import Page, { checkNotAuth } from '../components/Page';
import history from '../history';
import { nsOptions } from '../i18n';
import Toast from '../utils/Toast';
import ErrorUtil from '../utils/ErrorUtil';
import { setLanguage } from '../utils/language';
import { authActions, miscActions } from '../redux/actions';
import { WEBSITE_URL } from '../constants';
import NewTooltip from '../components/NewTooltip';
import isMaxLoginAttemptsExceeded from '../utils/auth';


const mapStateToProps = (state) => ({
  lastAuthUserId: state.auth.lastAuthUserId,
  pendingUrl: state.misc.pendingUrl,
});

const mapDispatchToProps = (dispatch) => ({
  auth: async (email, password) => dispatch(authActions.auth(email, password)),
  logout: async (resetStore) => dispatch(authActions.logout(resetStore)),
  clearPendingUrl: () => dispatch(miscActions.clearPendingUrl()),
});


@withToastManager
@connect(mapStateToProps, mapDispatchToProps)
@withTranslation('', nsOptions)
class Login extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    i18n: PropTypes.shape().isRequired,
    lastAuthUserId: PropTypes.number,
    auth: PropTypes.func.isRequired,
    logout: PropTypes.func.isRequired,
    clearPendingUrl: PropTypes.func.isRequired,
    pendingUrl: PropTypes.string,
  };

  static defaultProps = {
    lastAuthUserId: undefined,
    pendingUrl: undefined,
  };

  constructor(props) {
    super(props);
    this.mutex = new Mutex();
    this.state = {
      requires2FA: false,
      tempToken: null,
    };
  }

  onKeyPress = (event) => {
    if (event.key === 'Enter') this.handleFormSubmit(event);
  };

  handleFormSubmit = async (event) => {
    event.preventDefault();
    const { emailInput, passwordInput } = this;
    const {
      auth, logout, i18n, lastAuthUserId, clearPendingUrl,
    } = this.props;

    if (emailInput && passwordInput && !this.mutex.isLocked()) {
      const release = await this.mutex.acquire();

      try {
        const result = await auth(emailInput.value, passwordInput.value);

        // Check if 2FA verification is required (user has 2FA enabled)
        if (result && result.requires_2fa) {
          this.setState({
            requires2FA: true,
            tempToken: result.temp_token,
          });
          release();
          return;
        }

        // Normal auth flow (with optional/required 2FA setup)
        const user = result;
        if (user && user.last_password_change_date && moment().diff(moment(user.last_password_change_date), 'months') >= 12) {
          console.error('Your password is older than 12 months, please change it');
          await logout(true);
          history.push('/auth/recover?passwordExpired=true');
        } else {
          await setLanguage(i18n, user.language);
          if (lastAuthUserId && lastAuthUserId !== user.id) {
            clearPendingUrl();
          }
          Toast.success(this.props, 'error:valid.connected', 2000);

          // Check if user needs to set up 2FA
          const dismissedKey = `2fa_setup_dismissed_${user.id}`;
          const dismissedTimestamp = localStorage.getItem(dismissedKey);

          let shouldShowPrompt = user.requires_2fa_setup;
          // can_skip is false when 2FA is mandatory (policy = 'required')
          const canSkip = result.can_skip !== false;

          if (dismissedTimestamp && canSkip) {
            // Vérifier si le choix a été fait il y a moins de 30 jours
            // Only allow skipping if policy permits it
            const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
            const now = Date.now();
            const timeDiff = now - parseInt(dismissedTimestamp, 10);

            if (timeDiff < thirtyDaysInMs) {
              // Moins de 30 jours, on ne montre pas la page
              shouldShowPrompt = false;
            } else {
              // Plus de 30 jours, on supprime le localStorage et on redemande
              localStorage.removeItem(dismissedKey);
            }
          } else if (!canSkip) {
            // If can_skip is false (policy = required), always show the prompt
            // and clear any previous dismissal
            localStorage.removeItem(dismissedKey);
            shouldShowPrompt = true;
          }

          if (shouldShowPrompt) {
            // Store can_skip in sessionStorage for TwoFactorRequired page
            sessionStorage.setItem('2fa_can_skip', canSkip ? 'true' : 'false');
            history.push('/dashboard/2fa-required');
          } else {
            sessionStorage.removeItem('2fa_can_skip');
            history.push('/');
          }
        }
      } catch (error) {
        await logout(false);
        ErrorUtil.handleCatched(this.props, error);
        if (isMaxLoginAttemptsExceeded()) {
          history.push('/auth/recover');
        }
      } finally {
        release();
      }
    }
  };

  handle2FACancel = () => {
    this.setState({
      requires2FA: false,
      tempToken: null,
    });
    sessionStorage.removeItem('2fa-temp-token');
  };

  isInvitationPending = () => {
    const { pendingUrl } = this.props;
    if (pendingUrl) {
      const regex = new RegExp('^.*/dashboard/(join-project|join-team)/.*$');
      return pendingUrl.match(regex);
    }
    return false;
  };

  render() {
    const { t } = this.props;
    const { requires2FA, tempToken } = this.state;

    // If 2FA is required, show the TwoFactorLogin component
    if (requires2FA && tempToken) {
      return (
        <Page
          {...this.props}
          title={t('twoFactor:login.title')}
          checkAuthorizations={[checkNotAuth]}
        >
          <AuthContainer
            top={{
              info: '',
            }}
            bottom={{
              navContent: (
                <div className="row justify-content-center">
                  <div className="col-auto">
                    <button
                      className="btn btn-link"
                      onClick={this.handle2FACancel}
                    >
                      {t('twoFactor:button.back')}
                    </button>
                  </div>
                </div>
              ),
            }}
          >
            <TwoFactorLogin
              tempToken={tempToken}
              onCancel={this.handle2FACancel}
            />
          </AuthContainer>
        </Page>
      );
    }

    // Normal login form
    return (
      <Page
        {...this.props}
        title={t('auth:login')}
        checkAuthorizations={[checkNotAuth]}
      >
        <AuthContainer
          top={this.isInvitationPending() ? {
            subTitle: (
              <div className="mt-4">
                <FontAwesomeIcon icon={['fal', 'envelope-open-text']} transform="grow-6" />
                {' '}
                <div className="mt-3">
                  {t('auth:pages.login.invitation-pending.sub-title')}
                </div>
              </div>
            ),
            info: (
              <div className="mt-3 login-invitation-info">
                <span>
                  {t('auth:pages.login.invitation-pending.info-1')}
                </span>
                {' '}
                <NewTooltip content={t('auth:pages.login.invitation-pending.tooltip')}>
                  <Link className="text-underline text-newturquoise-1" to="/auth/register">
                    {t('auth:pages.login.invitation-pending.info-2')}
                  </Link>
                </NewTooltip>
                .
              </div>
            ),
          } : {
            info: t('auth:pages.login.info'),
          }}
          bottom={{
            navContent: (
              <div className="row justify-content-center">
                <div className="col-auto">
                  <button
                    className="btn btn-newturquoise-1"
                    onClick={this.handleFormSubmit}
                  >
                    {t('auth:button.login')}
                  </button>
                </div>
                <div className="col-12">
                  <div className="authentication-small-info">
                    <Link to="/auth/recover">
                      {t('auth:button.forgotten-password')}
                    </Link>
                    |
                    <Link to="/auth/register">
                      {t('auth:button.register-to-doqboard')}
                    </Link>
                    |
                    <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer">
                      {t('auth:button.visit-website')}
                    </a>
                  </div>
                </div>
              </div>
            ),
          }}
        >
          <div className="authentication-form px-0 px-sm-5 mx-auto">
            <LabeledInput
              type="email"
              name="email"
              label={t('user:email')}
              placeholder={t('error:placeholder.email')}
              required
              onValidChange={(e) => {
                this.emailInput = e.target;
              }}
              onKeyPress={this.onKeyPress}
              autoFocus
            />
            <LabeledInput
              type="password"
              name="password"
              label={t('user:password')}
              placeholder={t('error:placeholder.password')}
              required
              onValidChange={(e) => {
                this.passwordInput = e.target;
              }}
              onKeyPress={this.onKeyPress}
            />
          </div>
        </AuthContainer>
      </Page>
    );
  }
}


export default Login;
